/**
 * Block utility functions for coordinate extraction and property access.
 *
 * These utilities provide type-safe access to spatial properties across
 * different block types without requiring type casts.
 *
 * ## Unified Coordinate Extraction Pattern
 *
 * This module provides the **unified approach** for extracting x,y coordinates
 * from blocks. All code should use these utilities instead of inline patterns.
 *
 * ### Primary Functions
 *
 * 1. **`extractPoint(block)`** - Extract Point from spatial blocks
 *    - Use for: PlayerBlock, ControlPointBlock, AnnotationBlock, TextBlock, ShapeBlock, ImageBlock
 *    - Returns: `Point | null`
 *    - Type-safe: Works with SpatialBlock union type
 *
 * 2. **`extractPointFromProperties(properties)`** - Extract Point from raw properties object
 *    - Use for: Adapters, utilities working with raw property objects
 *    - Returns: `Point | null`
 *    - Type-safe: Validates properties contain x,y
 *
 * 3. **`extractSpatialProperties(block)`** - Extract Point + coordinate system
 *    - Use for: When coordinate system context is needed
 *    - Returns: `SpatialProperties | null` (includes coordinateSystem)
 *
 * ### Migration Guide
 *
 * **Before (inline pattern - AVOID):**
 * ```typescript
 * const x = (block.properties as any).x ?? 0
 * const y = (block.properties as any).y ?? 0
 * ```
 *
 * **After (unified approach - USE):**
 * ```typescript
 * import { extractPoint } from '@/utils/Block.utils'
 * const point = extractPoint(block)
 * const x = point?.x ?? 0
 * const y = point?.y ?? 0
 * ```
 *
 * ### Related Functions
 *
 * - `extractSpatialProperties` in `PropertiesHelper.ts` - Different signature (takes properties object, includes coordinate system validation)
 */

import type { Point } from '@/components/blocks/Types'
import type { PlayBlock_Union, BlockBase } from '@/types/blocks'
import type { PlayerBlock } from '@/types/blocks/canvas/Player'
import type { ControlPointBlock } from '@/types/blocks/canvas/ControlPoint'
import type { AnnotationBlock } from '@/types/blocks/canvas/Annotation'
import type { TextBlock } from '@/types/blocks/document/Text'
import type { ShapeBlock } from '@/types/blocks/document/Shape'
import type { ImageBlock } from '@/types/blocks/document/Image'
import type { CoordinateSystem } from '@/types/BlockProperties'

/**
 * Union type of all block types that have x,y properties.
 */
export type SpatialBlock =
	| PlayerBlock
	| ControlPointBlock
	| AnnotationBlock
	| TextBlock
	| ShapeBlock
	| ImageBlock

/**
 * Type guard to check if a block has spatial properties (x, y coordinates).
 *
 * @param block - Any block from the PlayBlock_Union
 * @returns true if block has x,y properties
 *
 * @example
 * if (isSpatialBlock(block)) {
 *   const point = extractPoint(block)
 * }
 */
export function isSpatialBlock(block: PlayBlock_Union): block is SpatialBlock {
	return block.type === 'player' ||
		block.type === 'control_point' ||
		block.type === 'annotation' ||
		block.type === 'text' ||
		block.type === 'shape' ||
		block.type === 'image'
}

/**
 * Extract x,y coordinates from any spatial block as a Point.
 * Returns null if block is null/undefined or doesn't have spatial properties.
 * Defaults to (0, 0) for blocks that have the spatial properties but values are undefined.
 *
 * @param block - Any spatial block (player, control_point, annotation, text, shape, image)
 * @returns Point with x,y coordinates, or null if block is invalid
 *
 * @example
 * const position = extractPoint(playerBlock)
 * if (position) {
 *   console.log(`Position: ${position.x}, ${position.y}`)
 * }
 *
 * @example
 * // Safe to use with any block type
 * const point = extractPoint(block)
 * const x = point?.x ?? 0
 * const y = point?.y ?? 0
 */
export function extractPoint(block: SpatialBlock | null | undefined): Point | null {
	if (!block) {
		return null
	}

	// All spatial blocks have x,y in their properties
	const { properties } = block
	return {
		x: properties.x ?? 0,
		y: properties.y ?? 0,
	}
}

/**
 * Extract x,y coordinates from a generic properties object.
 * Useful for adapters and utilities that work with raw property objects.
 *
 * @param properties - Properties object that may contain x,y coordinates
 * @returns Point with x,y coordinates, or null if properties don't include spatial data
 *
 * @example
 * const spatialProps = block.properties as { x?: number; y?: number }
 * const point = extractPointFromProperties(spatialProps)
 */
export function extractPointFromProperties(
	properties: Record<string, unknown>
): Point | null {
	if (
		typeof properties.x !== 'number' &&
		typeof properties.y !== 'number'
	) {
		return null
	}

	return {
		x: (properties.x as number | undefined) ?? 0,
		y: (properties.y as number | undefined) ?? 0,
	}
}

/**
 * Extended spatial properties including coordinate system context.
 */
export interface SpatialProperties {
	x: number
	y: number
	coordinateSystem: CoordinateSystem
}

/**
 * Extract full spatial properties including coordinate system.
 * Returns null if the block doesn't have a coordinate system defined.
 *
 * @param block - Any spatial block
 * @returns Full spatial properties with coordinate system, or null
 *
 * @example
 * const spatial = extractSpatialProperties(block)
 * if (spatial?.coordinateSystem === 'field') {
 *   // Handle field coordinates
 * }
 */
export function extractSpatialProperties(
	block: SpatialBlock | null | undefined
): SpatialProperties | null {
	if (!block) {
		return null
	}

	const { properties } = block
	const coordinateSystem = properties.coordinateSystem

	if (!coordinateSystem) {
		return null
	}

	return {
		x: properties.x ?? 0,
		y: properties.y ?? 0,
		coordinateSystem,
	}
}

/**
 * Update x,y coordinates in a block's properties object.
 * Returns a new properties object with updated coordinates.
 *
 * @param properties - Existing properties object
 * @param point - New x,y coordinates
 * @returns Updated properties object
 *
 * @example
 * const newProperties = setPoint(block.properties, { x: 10, y: 20 })
 * onUpdate(block.id, newProperties)
 */
export function setPoint<T extends Record<string, unknown>>(
	properties: T,
	point: Point
): T {
	return {
		...properties,
		x: point.x,
		y: point.y,
	}
}

/**
 * Extract root_block_id from any block type.
 * TypeScript doesn't preserve BlockBase properties on unions,
 * so we use this helper for type-safe access.
 *
 * @param block - Any block object (runtime has root_block_id even if TS type doesn't)
 * @returns The root_block_id string
 *
 * @example
 * const rootId = getRootBlockId(block)
 * const playbookId = await playbookContents.getPlaybookForBlock(rootId)
 */
export function getRootBlockId(block: unknown): string {
	const rootId = (block as { root_block_id?: string }).root_block_id
	if (!rootId) {
		throw new Error('Block missing root_block_id property')
	}
	return rootId
}
